import { computed }                       from 'mobx';
import { makeAutoObservable, observable } from 'mobx';
import { PagedCollection }                from 'collections/PagedCollection';
import { ApiCollection }                  from 'collections/ApiCollection';
import DemandModel                        from 'models/DemandModel';
import DemandStatsModel                   from 'models/DemandStatsModel';
import DemandTypeModel                    from 'models/DemandTypeModel';
import ShowModel                          from 'models/ShowModel';
import StandModel                         from 'models/StandModel';
import currentFilters                     from 'utils/currentFilters';
import DemandStatusModel                  from 'models/DemandStatusModel';
import currentSession                     from 'utils/currentSession';

const ITEMS_PER_PAGE = 50;

export default class DemandListStore {
  @observable
  public rawFilters: Record<string, any> = {};

  @observable
  public sorts: Record<string, any> = {};

  @observable public demandCollection = new PagedCollection(DemandModel);
  @observable public demandCollectionFull = new PagedCollection(DemandModel);
  @observable public demandStats = new DemandStatsModel();
  @observable public demandStatusCollection = new ApiCollection(DemandStatusModel);
  @observable public demandTypeCollection = new ApiCollection(DemandTypeModel);
  @observable public show = new ShowModel();
  @observable public standCollection = new ApiCollection(StandModel);
  @observable public standCollectionFull = new ApiCollection(StandModel);

  constructor() {
    makeAutoObservable(this);
  }

  public clear = () => {
    this.demandCollection.clear();
    this.demandCollectionFull.clear();
    this.demandStats.clear();
    this.demandStatusCollection.clear();
    this.demandTypeCollection.clear();
    this.show.clear();
    this.standCollection.clear();
    this.standCollectionFull.clear();
  };

  public fetchDemandsData = async (
    showId: id,
    pagination: { current?: number, pageSize?: number },
    apiFilters: DemandModel['filters'],
    sorter: Record<string, 'asc' | 'desc'>,
  ) => {
    this.demandCollection.itemsPerPage = pagination.pageSize || ITEMS_PER_PAGE;
    this.demandCollection.setPage(pagination.current || 1);

    // remove all filters
    this.demandCollection.filters = {};

    // set filters
    Object.keys(apiFilters).forEach(filterName => {
      const key = filterName as keyof DemandModel['filters'];
      this.demandCollection.setFilter(key, apiFilters[key]);
    });

    this.demandCollection.setFilter('stand.show', showId);

    // remove all sorts
    this.demandCollection.sorts = {};

    // set sorts
    Object.keys(sorter).forEach(sortName => {
      this.demandCollection.setSort(sortName, sorter[sortName] === 'asc');
    });

    this.demandCollectionFull.itemsPerPage = 1000;
    this.demandCollectionFull.setFilters({
      ...this.demandCollection.filters,
      properties: ['id', 'stand', 'treated'],
    });

    await this.demandCollection.list();

    const promises: Promise<any>[] = [
      this.demandCollectionFull
        .list()
        .then(async () => {
          const standsIdsFull = this.demandCollectionFull.getUniques('stand.id');

          if (standsIdsFull.length) {
            this.standCollectionFull.filters = {
              id: this.demandCollectionFull.getUniques('stand.id'),
              itemsPerPage: 1000,
              // pagination: false,
              properties: ['hall', 'treated'],
            };

            await this.standCollectionFull.list();
          }
        }),

        this.demandStats
          .fetch({
            params: {
              'stand.show': showId,
            },
          }),
    ];

    const standsIds = this.demandCollection.getUniques('stand.id');

    if (standsIds.length) {
      promises.push(
        this.standCollection
          .setFilter('id', standsIds)
          .list(),
      );
    }

    await Promise.all(promises);
  }

  public fetchTypesAndStatuses = async () => {
    await Promise.all([
      this.demandStatusCollection.list(),
      this.demandTypeCollection.list(),
    ]);
  };

  public initFromLocalStorage = async () => {
    if (!currentFilters.isLoaded) {
      await currentFilters.load(currentSession.user.user_id);
    }
    this.rawFilters = currentFilters.getFiltersForPath(window.location.pathname);
    this.sorts = currentFilters.getSortsForPath(window.location.pathname);
  }

  public load = async (
    showId: id,
    pagination: { current?: number, pageSize?: number },
    apiFilters: DemandModel['filters'],
    sorter: Record<string, 'asc' | 'desc'>,
  ) => {
    await Promise.all([
      this.fetchDemandsData(showId, pagination, apiFilters, sorter),

      this.show
        .set('id', showId)
        .fetch(),

      this.fetchTypesAndStatuses(),
    ])
  };

  public setAndSaveRawFilters = async (filters: Record<string, any>, sorts?: any) => {
    this.rawFilters = filters;
    if (sorts) this.sorts = sorts;

    await currentFilters.saveFiltersAndSorts(
      window.location.pathname,
      this.rawFilters,
      this.sorts
    );
  }

  @computed
  public get apiFilters() {
    return this._processFilters(this.rawFilters);
  }

  private _processFilters = (filters: Record<string, any>): DemandModel['filters'] => {
    const processed = { ...filters };

    if (processed['stand.surface']) {
      const surfaceValues = Array.isArray(processed['stand.surface']) ? processed['stand.surface'] : [processed['stand.surface']];
      let min: number | undefined;
      let max: number | undefined;

      surfaceValues.forEach((value: string) => {
        if (typeof value === 'string' && value.includes('_to_')) {
          const [vMin, vMax] = value.split('_to_').map(v => v ? parseInt(v, 10) : undefined);

          if (vMin !== undefined && (min === undefined || vMin < min)) {
            min = vMin;
          }
          if (vMax !== undefined && (max === undefined || vMax > max)) {
            max = vMax;
          } else if (value.endsWith('_to_')) {
            // Cas particulier pour "+50" (51_to_) : on ne veut plus de borne max
            max = undefined;
          }
        }
      });

      processed['stand.surface[gte]'] = min;
      processed['stand.surface[lte]'] = max;

      delete processed['stand.surface'];
    }

    return processed;
  };
}
